import React, { useState, useEffect, useRef } from 'react';
import './Carousel.scss';

interface CarouselItem {
    image: string;
    destination?: string;
}

interface CarouselProps {
    items: CarouselItem[];
    autoPlay?: boolean;
    autoPlayInterval?: number;
    showDots?: boolean;
    showArrows?: boolean;
    className?: string;
}

const Carousel: React.FC<CarouselProps> = ({
    items,
    autoPlay = true,
    autoPlayInterval = 5000,
    showDots = true,
    showArrows = true,
    className
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    // Auto-play functionality
    useEffect(() => {
        if (autoPlay && items.length > 1) {
            intervalRef.current = setInterval(() => {
                handleNext();
            }, autoPlayInterval);

            return () => {
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                }
            };
        }
    }, [autoPlay, autoPlayInterval, currentIndex, items.length]);

    const handlePrevious = () => {
        if (isTransitioning) return;
        setIsTransitioning(true);
        setCurrentIndex(prevIndex =>
            prevIndex === 0 ? items.length - 1 : prevIndex - 1
        );
        setTimeout(() => setIsTransitioning(false), 300);
    };

    const handleNext = () => {
        if (isTransitioning) return;
        setIsTransitioning(true);
        setCurrentIndex(prevIndex =>
            prevIndex === items.length - 1 ? 0 : prevIndex + 1
        );
        setTimeout(() => setIsTransitioning(false), 300);
    };

    const handleDotClick = (index: number) => {
        if (isTransitioning || index === currentIndex) return;
        setIsTransitioning(true);
        setCurrentIndex(index);
        setTimeout(() => setIsTransitioning(false), 300);
    };

    const handleImageClick = () => {
        const destination = items[currentIndex]?.destination;
        if (!destination) {
            return;
        }
        if (destination.startsWith('http')) {
            window.open(destination, '_blank');
        } else {
            window.location.href = destination;
        }
    };

    if (!items || items.length === 0) {
        return null;
    }

    return (
        <div className={`carousel-container ${className || ''}`}>
            <div className="carousel-wrapper">
                <div className="carousel-inner">
                    {items.map((item, index) => (
                        <div
                            key={index}
                            className={`carousel-slide ${
                                index === currentIndex ? 'active' : ''
                            }`}
                            style={{
                                transform: `translateX(${(index - currentIndex) * 100}%)`,
                            }}
                        >
                            <img
                                src={item.image}
                                alt={`Slide ${index + 1}`}
                                className="carousel-image"
                                onClick={item.destination ? handleImageClick : undefined}
                                style={{ cursor: item.destination ? 'pointer' : 'default' }}
                            />
                        </div>
                    ))}
                </div>

                {/* Navigation Arrows */}
                {showArrows && items.length > 1 && (
                    <>
                        <button
                            className="carousel-arrow carousel-arrow-prev"
                            onClick={handlePrevious}
                            aria-label="Previous slide"
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M15 18L9 12L15 6"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                        <button
                            className="carousel-arrow carousel-arrow-next"
                            onClick={handleNext}
                            aria-label="Next slide"
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M9 18L15 12L9 6"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                    </>
                )}
            </div>

            {/* Dots Indicator */}
            {showDots && items.length > 1 && (
                <div className="carousel-dots">
                    {items.map((_, index) => (
                        <button
                            key={index}
                            className={`carousel-dot ${
                                index === currentIndex ? 'active' : ''
                            }`}
                            onClick={() => handleDotClick(index)}
                            aria-label={`Go to slide ${index + 1}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default Carousel;